import React, { useState, useEffect, useMemo } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Table,
  Badge,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import { API_WEB_URLS } from "../../constants/constAPI";
import toastr from "toastr";

const PageList_CustomNotification = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterAudience, setFilterAudience] = useState("ALL");
  const [filterCategory, setFilterCategory] = useState("ALL");

  // Summary & Preview Modal State
  const [viewModal, setViewModal] = useState({ show: false, campaign: null });

  // Detailed Per-Device Delivery Audit Report Modal State
  const [reportModal, setReportModal] = useState({
    show: false,
    campaign: null,
    loading: false,
    totalAttempted: 0,
    successCount: 0,
    failureCount: 0,
    logs: [],
    filterStatus: "ALL", // ALL, SUCCESS, FAILED
    searchTerm: "",
  });

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const url = `${API_WEB_URLS.CUSTOM_NOTIFICATION}/0/token/List`;
      const res = await axios.get(url, { timeout: 15000 });
      let list = [];
      if (res?.data?.data?.DataList) {
        list = res.data.data.DataList;
      } else if (res?.data?.data?.dataList) {
        list = res.data.data.dataList;
      } else if (res?.data?.DataList) {
        list = res.data.DataList;
      } else if (Array.isArray(res?.data?.data)) {
        list = res.data.data;
      } else if (Array.isArray(res?.data)) {
        list = res.data;
      }
      setCampaigns(list || []);
    } catch (err) {
      console.error("Error fetching notification campaigns from DB:", err);
      toastr.error("Could not load campaigns from database.", "Database Error");
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  const openReportModal = async (campaign) => {
    setReportModal({
      show: true,
      campaign,
      loading: true,
      totalAttempted: 0,
      successCount: 0,
      failureCount: 0,
      logs: [],
      filterStatus: "ALL",
      searchTerm: "",
    });

    try {
      const url = `${API_WEB_URLS.CUSTOM_NOTIFICATION}/0/token/CampaignLogs/${campaign.Id}`;
      const res = await axios.get(url, { timeout: 15000 });
      const data = res?.data?.data || res?.data;
      setReportModal((prev) => ({
        ...prev,
        loading: false,
        totalAttempted: data?.TotalAttempted || 0,
        successCount: data?.SuccessCount || 0,
        failureCount: data?.FailureCount || 0,
        logs: data?.Logs || [],
      }));
    } catch (err) {
      console.error("Error fetching campaign logs:", err);
      toastr.error("Failed to load device delivery audit logs.", "Error");
      setReportModal((prev) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  // Metrics computation
  const metrics = useMemo(() => {
    const list = campaigns || [];
    const total = list.length;
    const customers = list.filter((c) => c.AudienceType === "Customer").length;
    const drivers = list.filter((c) => c.AudienceType === "Driver").length;
    const totalReached = list.reduce((acc, c) => acc + (Number(c.TotalRecipients) || 0), 0);
    return { total, customers, drivers, totalReached };
  }, [campaigns]);

  // Filtered List
  const filteredCampaigns = useMemo(() => {
    return (campaigns || []).filter((c) => {
      const matchSearch =
        !searchTerm ||
        (c.Title && c.Title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (c.Body && c.Body.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchAudience =
        filterAudience === "ALL" || c.AudienceType === filterAudience;

      const matchCategory =
        filterCategory === "ALL" || c.Category === filterCategory;

      return matchSearch && matchAudience && matchCategory;
    });
  }, [campaigns, searchTerm, filterAudience, filterCategory]);

  // Filtered Logs for Device Delivery Audit Modal
  const filteredLogs = useMemo(() => {
    let list = reportModal.logs || [];
    if (reportModal.filterStatus === "SUCCESS") {
      list = list.filter((l) => String(l.Status).toUpperCase() === "SUCCESS");
    } else if (reportModal.filterStatus === "FAILED") {
      list = list.filter((l) => String(l.Status).toUpperCase() === "FAILED");
    }

    if (reportModal.searchTerm && reportModal.searchTerm.trim()) {
      const q = reportModal.searchTerm.toLowerCase().trim();
      list = list.filter(
        (l) =>
          (l.MemberName && String(l.MemberName).toLowerCase().includes(q)) ||
          (l.MobileNo && String(l.MobileNo).includes(q)) ||
          (l.FCMToken && String(l.FCMToken).toLowerCase().includes(q)) ||
          (l.ErrorCode && String(l.ErrorCode).toLowerCase().includes(q)) ||
          (l.ErrorMessage && String(l.ErrorMessage).toLowerCase().includes(q))
      );
    }
    return list;
  }, [reportModal.logs, reportModal.filterStatus, reportModal.searchTerm]);

  const handleDuplicate = (campaign) => {
    navigate("/SendNotification", { state: { campaign } });
  };

  const formatConditionName = (c) => {
    if (!c) return "-";
    if (c.ConditionType === "ALL") return "🌐 All Selected Audience";
    if (c.ConditionType === "NEW_USERS") return `✨ New Users (${c.ConditionDays || 7}d)`;
    if (c.ConditionType === "INACTIVE_USERS") return `⏳ Inactive (${c.ConditionDays || 15}d)`;
    if (c.ConditionType === "WALLET_BALANCE") return `💰 Balance ${c.ConditionValue || "<=100"}`;
    if (c.ConditionType === "DRIVER_KYC") return `📑 KYC: ${c.ConditionValue || "Pending"}`;
    if (c.ConditionType === "SPECIFIC_USERS") return "🎯 Specific Members";
    if (c.ConditionType === "CUSTOM_MOBILES") return "📱 Custom Mobile Numbers";
    return c.ConditionType;
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        {/* Header */}
        <Row className="align-items-center mb-3">
          <Col md={6}>
            <h4 className="font-size-18 mb-1 font-weight-bold text-dark">
              <i className="bx bx-history text-primary me-2"></i>
              Notification Campaign History & Logs
            </h4>
            <p className="text-muted mb-0 font-size-13">
              Track delivery performance, recipient reach, and broadcast logs for Customers & Drivers.
            </p>
          </Col>
          <Col md={6} className="text-md-end mt-2 mt-md-0">
            <Button
              color="light"
              size="sm"
              className="me-2 rounded-pill px-3 shadow-sm border"
              onClick={fetchCampaigns}
            >
              <i className="bx bx-refresh me-1"></i> Refresh
            </Button>
            <Link
              to="/SendNotification"
              className="btn btn-primary btn-sm rounded-pill px-3 shadow-sm"
            >
              <i className="bx bx-plus me-1"></i> Create New Notification
            </Link>
          </Col>
        </Row>

        {/* Metric KPI Cards */}
        <Row className="mb-3">
          <Col md={3} sm={6}>
            <Card className="shadow-sm border-0 bg-white">
              <CardBody className="p-3">
                <div className="d-flex align-items-center">
                  <div className="avatar-sm bg-soft-primary text-primary rounded-circle d-flex align-items-center justify-content-center me-3">
                    <i className="bx bx-broadcast font-size-22"></i>
                  </div>
                  <div>
                    <span className="text-muted font-size-12">Total Broadcasts</span>
                    <h5 className="font-size-18 mb-0 font-weight-bold">{metrics.total}</h5>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col md={3} sm={6}>
            <Card className="shadow-sm border-0 bg-white">
              <CardBody className="p-3">
                <div className="d-flex align-items-center">
                  <div className="avatar-sm bg-soft-info text-info rounded-circle d-flex align-items-center justify-content-center me-3">
                    <i className="bx bx-user font-size-22"></i>
                  </div>
                  <div>
                    <span className="text-muted font-size-12">Customer Campaigns</span>
                    <h5 className="font-size-18 mb-0 font-weight-bold">{metrics.customers}</h5>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col md={3} sm={6}>
            <Card className="shadow-sm border-0 bg-white">
              <CardBody className="p-3">
                <div className="d-flex align-items-center">
                  <div className="avatar-sm bg-soft-warning text-warning rounded-circle d-flex align-items-center justify-content-center me-3">
                    <i className="bx bx-car font-size-22"></i>
                  </div>
                  <div>
                    <span className="text-muted font-size-12">Captain Campaigns</span>
                    <h5 className="font-size-18 mb-0 font-weight-bold">{metrics.drivers}</h5>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col md={3} sm={6}>
            <Card className="shadow-sm border-0 bg-white">
              <CardBody className="p-3">
                <div className="d-flex align-items-center">
                  <div className="avatar-sm bg-soft-success text-success rounded-circle d-flex align-items-center justify-content-center me-3">
                    <i className="bx bx-check-double font-size-22"></i>
                  </div>
                  <div>
                    <span className="text-muted font-size-12">Total Reached Users</span>
                    <h5 className="font-size-18 mb-0 font-weight-bold">
                      {metrics.totalReached.toLocaleString()}
                    </h5>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Filter & Search Toolbar */}
        <Card className="shadow-sm border-0 mb-3">
          <CardBody className="py-2 px-3">
            <Row className="g-2 align-items-center">
              <Col md={5}>
                <div className="position-relative">
                  <Input
                    type="text"
                    className="form-control form-control-sm ps-4"
                    placeholder="Search by campaign title or content..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <i
                    className="bx bx-search position-absolute top-50 start-0 translate-middle-y ms-2 text-muted"
                  ></i>
                </div>
              </Col>

              <Col md={3}>
                <Input
                  type="select"
                  className="form-select form-select-sm"
                  value={filterAudience}
                  onChange={(e) => setFilterAudience(e.target.value)}
                >
                  <option value="ALL">All Audiences (Customer & Driver)</option>
                  <option value="Customer">👤 Customers Only</option>
                  <option value="Driver">🚗 Captains / Drivers Only</option>
                  <option value="Both">👥 Both (Broadcast)</option>
                </Input>
              </Col>

              <Col md={3}>
                <Input
                  type="select"
                  className="form-select form-select-sm"
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  <option value="ALL">All Categories</option>
                  <option value="PROMOTIONAL">🎉 Promotional</option>
                  <option value="ALERT">🚨 Critical Alert</option>
                  <option value="WALLET_OFFER">💰 Wallet & Cashback</option>
                  <option value="KYC_ACCOUNT">📑 KYC & Account</option>
                </Input>
              </Col>

              <Col md={1} className="text-end">
                <Button
                  size="sm"
                  color="light"
                  className="w-100 border font-size-11"
                  onClick={() => {
                    setSearchTerm("");
                    setFilterAudience("ALL");
                    setFilterCategory("ALL");
                  }}
                >
                  Reset
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Campaign List Table */}
        <Card className="shadow-sm border-0">
          <CardBody className="p-0">
            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2 text-muted font-size-13">Loading campaign logs...</p>
              </div>
            ) : filteredCampaigns.length === 0 ? (
              <div className="text-center py-5">
                <i className="bx bx-bell-off font-size-48 text-muted mb-2"></i>
                <h5 className="font-size-15 font-weight-bold">No Notification Campaigns Found</h5>
                <p className="text-muted font-size-13">
                  Click below to compose and broadcast your first custom notification.
                </p>
                <Link to="/SendNotification" className="btn btn-primary btn-sm rounded-pill px-3">
                  + Create Notification
                </Link>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover className="table-centered mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "60px" }}>#</th>
                      <th>Campaign Title & Message</th>
                      <th>Target Audience</th>
                      <th>Condition / Filter</th>
                      <th>Recipients</th>
                      <th>Sent At</th>
                      <th>Status</th>
                      <th className="text-end" style={{ width: "140px" }}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCampaigns.map((c, index) => (
                      <tr key={c.Id || index}>
                        <td className="font-size-12 text-muted">{index + 1}</td>
                        <td style={{ maxWidth: "320px" }}>
                          <div className="d-flex align-items-center">
                            {c.ImageUrl ? (
                              <img
                                src={c.ImageUrl}
                                alt="Thumb"
                                className="avatar-xs rounded me-2 object-cover"
                                style={{ width: "36px", height: "36px", objectFit: "cover" }}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : (
                              <div className="avatar-xs bg-soft-primary text-primary rounded d-flex align-items-center justify-content-center me-2 flex-shrink-0">
                                <i className="bx bx-bell font-size-16"></i>
                              </div>
                            )}
                            <div className="text-truncate">
                              <h6 className="font-size-13 mb-0 text-dark font-weight-bold text-truncate">
                                {c.Title}
                              </h6>
                              <p className="text-muted font-size-11 mb-0 text-truncate">
                                {c.Body}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td>
                          {c.AudienceType === "Driver" ? (
                            <Badge color="warning" className="p-1 px-2 rounded-pill font-size-11">
                              <i className="bx bx-car me-1"></i> Captains / Drivers
                            </Badge>
                          ) : c.AudienceType === "Customer" ? (
                            <Badge color="primary" className="p-1 px-2 rounded-pill font-size-11">
                              <i className="bx bx-user me-1"></i> Customers / Riders
                            </Badge>
                          ) : (
                            <Badge color="success" className="p-1 px-2 rounded-pill font-size-11">
                              <i className="bx bx-broadcast me-1"></i> All Members
                            </Badge>
                          )}
                        </td>
                        <td>
                          <span className="font-size-12 text-secondary">
                            {formatConditionName(c)}
                          </span>
                          {c.CityName && (
                            <small className="d-block text-muted font-size-11">
                              📍 {c.CityName}
                            </small>
                          )}
                        </td>
                        <td>
                          <Badge color="light" className="text-dark border font-size-12 px-2 py-1">
                            <i className="bx bx-group me-1 text-primary"></i>
                            {(Number(c.TotalRecipients) || 0).toLocaleString()}
                          </Badge>
                        </td>
                        <td className="font-size-12 text-muted">
                          {c.CreatedDate ? new Date(c.CreatedDate).toLocaleString("en-IN") : "-"}
                        </td>
                        <td>
                          <Badge
                            color={c.SendStatus === "SCHEDULED" ? "info" : "success"}
                            className="p-1 px-2 font-size-11 rounded-pill"
                          >
                            {c.SendStatus === "SCHEDULED" ? "⏳ Scheduled" : "✅ Sent"}
                          </Badge>
                        </td>
                        <td className="text-end" style={{ whiteSpace: "nowrap" }}>
                          <Button
                            size="sm"
                            color="info"
                            outline
                            className="btn-icon me-1"
                            title="Detailed Device Delivery Audit (विस्तृत डिवाइस रिपोर्ट)"
                            onClick={() => openReportModal(c)}
                          >
                            <i className="bx bx-bar-chart-alt-2 font-size-14"></i>
                          </Button>
                          <Button
                            size="sm"
                            color="light"
                            className="btn-icon me-1 text-primary border"
                            title="View Full Details"
                            onClick={() => setViewModal({ show: true, campaign: c })}
                          >
                            <i className="bx bx-show font-size-14"></i>
                          </Button>
                          <Button
                            size="sm"
                            color="light"
                            className="btn-icon text-success border"
                            title="Duplicate / Resend Campaign"
                            onClick={() => handleDuplicate(c)}
                          >
                            <i className="bx bx-copy font-size-14"></i>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>

        {/* View Campaign Details Modal */}
        <Modal
          isOpen={viewModal.show}
          toggle={() => setViewModal({ show: false, campaign: null })}
          centered
          size="lg"
        >
          <ModalHeader toggle={() => setViewModal({ show: false, campaign: null })}>
            <i className="bx bx-broadcast text-primary me-2"></i>
            Campaign Summary & Message Details
          </ModalHeader>
          <ModalBody className="p-4">
            {viewModal.campaign && (
              <Row>
                <Col md={7}>
                  <div className="mb-3">
                    <span className="text-muted font-size-11 text-uppercase font-weight-bold">
                      Notification Title
                    </span>
                    <h5 className="font-size-16 font-weight-bold text-dark mt-1">
                      {viewModal.campaign.Title}
                    </h5>
                  </div>

                  <div className="mb-3">
                    <span className="text-muted font-size-11 text-uppercase font-weight-bold">
                      Message Content
                    </span>
                    <p className="font-size-13 text-secondary bg-light p-3 rounded border mt-1">
                      {viewModal.campaign.Body}
                    </p>
                  </div>

                  <Row className="g-2 mb-3">
                    <Col sm={6}>
                      <span className="text-muted font-size-11 text-uppercase">Target Audience</span>
                      <div className="font-weight-bold font-size-13 mt-1">
                        {viewModal.campaign.AudienceType}
                      </div>
                    </Col>
                    <Col sm={6}>
                      <span className="text-muted font-size-11 text-uppercase">Target Condition</span>
                      <div className="font-weight-bold font-size-13 mt-1">
                        {formatConditionName(viewModal.campaign)}
                      </div>
                    </Col>
                    <Col sm={6}>
                      <span className="text-muted font-size-11 text-uppercase">Total Recipients</span>
                      <div className="font-weight-bold font-size-13 mt-1 text-success">
                        {(Number(viewModal.campaign.TotalRecipients) || 0).toLocaleString()} users
                      </div>
                    </Col>
                    <Col sm={6}>
                      <span className="text-muted font-size-11 text-uppercase">Deep-Link Target</span>
                      <div className="font-weight-bold font-size-13 mt-1 text-primary">
                        {viewModal.campaign.ActionRoute || "HOME"}
                      </div>
                    </Col>
                  </Row>
                </Col>

                <Col md={5} className="text-center">
                  <span className="text-muted font-size-11 text-uppercase font-weight-bold d-block mb-2">
                    Mobile Push Preview
                  </span>
                  <div
                    className="p-3 rounded border text-start shadow-sm"
                    style={{ backgroundColor: "#f8f9fa" }}
                  >
                    <div className="d-flex align-items-center mb-2">
                      <div className="avatar-xs bg-primary text-white rounded d-flex align-items-center justify-content-center me-2">
                        <i className="bx bx-navigation"></i>
                      </div>
                      <span className="font-size-11 font-weight-bold">KIRAYAGO</span>
                      <span className="ms-auto font-size-10 text-muted">now</span>
                    </div>

                    <div className="font-size-12 font-weight-bold text-dark mb-1">
                      {viewModal.campaign.Title}
                    </div>
                    <div className="font-size-11 text-muted mb-2">
                      {viewModal.campaign.Body}
                    </div>

                    {viewModal.campaign.ImageUrl && (
                      <img
                        src={viewModal.campaign.ImageUrl}
                        alt="Preview"
                        className="w-100 rounded mb-2"
                        style={{ height: "90px", objectFit: "cover" }}
                      />
                    )}

                    {viewModal.campaign.ActionLabel && (
                      <span className="badge bg-primary font-size-10">
                        {viewModal.campaign.ActionLabel} &rarr;
                      </span>
                    )}
                  </div>
                </Col>
              </Row>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              size="sm"
              onClick={() => {
                const c = viewModal.campaign;
                setViewModal({ show: false, campaign: null });
                handleDuplicate(c);
              }}
            >
              <i className="bx bx-copy me-1"></i> Duplicate & Send Again
            </Button>
            <Button
              color="secondary"
              size="sm"
              onClick={() => setViewModal({ show: false, campaign: null })}
            >
              Close
            </Button>
          </ModalFooter>
        </Modal>

        {/* Detailed Per-Device Delivery Audit Report Modal */}
        <Modal
          isOpen={reportModal.show}
          toggle={() => setReportModal((prev) => ({ ...prev, show: false }))}
          centered
          size="xl"
        >
          <ModalHeader
            toggle={() => setReportModal((prev) => ({ ...prev, show: false }))}
            className="bg-light"
          >
            <div className="d-flex align-items-center">
              <div className="avatar-xs bg-primary text-white rounded d-flex align-items-center justify-content-center me-2">
                <i className="bx bx-bar-chart-alt-2 font-size-16"></i>
              </div>
              <div>
                <h5 className="font-size-15 mb-0 font-weight-bold">
                  Device Delivery Audit Report
                </h5>
                <small className="text-muted font-size-12">
                  Campaign #{reportModal.campaign?.Id}: "{reportModal.campaign?.Title}"
                </small>
              </div>
            </div>
          </ModalHeader>
          <ModalBody className="p-4">
            {reportModal.loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2 text-muted font-size-13">Loading per-device delivery audit logs from database...</p>
              </div>
            ) : (
              <>
                {/* KPI Summary Cards */}
                <Row className="g-3 mb-4">
                  <Col md={4}>
                    <div className="p-3 rounded border bg-light d-flex align-items-center">
                      <div className="avatar-sm bg-soft-primary text-primary rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0">
                        <i className="bx bx-devices font-size-22"></i>
                      </div>
                      <div>
                        <span className="text-muted font-size-12">Total Devices Dispatched</span>
                        <h4 className="font-size-20 mb-0 font-weight-bold text-dark">
                          {reportModal.totalAttempted}
                        </h4>
                      </div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="p-3 rounded border bg-soft-success d-flex align-items-center border-success">
                      <div className="avatar-sm bg-success text-white rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0">
                        <i className="bx bx-check-circle font-size-22"></i>
                      </div>
                      <div>
                        <span className="text-success font-size-12 font-weight-bold">Succeeded Devices</span>
                        <h4 className="font-size-20 mb-0 font-weight-bold text-success">
                          {reportModal.successCount}
                        </h4>
                        <small className="text-muted font-size-11">Delivered to Google FCM Server</small>
                      </div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="p-3 rounded border bg-soft-danger d-flex align-items-center border-danger">
                      <div className="avatar-sm bg-danger text-white rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0">
                        <i className="bx bx-x-circle font-size-22"></i>
                      </div>
                      <div>
                        <span className="text-danger font-size-12 font-weight-bold">Failed Devices</span>
                        <h4 className="font-size-20 mb-0 font-weight-bold text-danger">
                          {reportModal.failureCount}
                        </h4>
                        <small className="text-muted font-size-11">App uninstalled / expired token</small>
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* Filter and Search Bar */}
                <Row className="g-2 mb-3 align-items-center">
                  <Col md={6}>
                    <div className="position-relative">
                      <Input
                        type="text"
                        className="form-control form-control-sm ps-4"
                        placeholder="Search by recipient name, mobile no, or error reason..."
                        value={reportModal.searchTerm}
                        onChange={(e) =>
                          setReportModal((prev) => ({ ...prev, searchTerm: e.target.value }))
                        }
                      />
                      <i className="bx bx-search position-absolute top-50 start-0 translate-middle-y ms-2 text-muted"></i>
                    </div>
                  </Col>
                  <Col md={6} className="text-md-end">
                    <div className="btn-group btn-group-sm">
                      <Button
                        color={reportModal.filterStatus === "ALL" ? "primary" : "light"}
                        className="border font-size-11"
                        onClick={() => setReportModal((prev) => ({ ...prev, filterStatus: "ALL" }))}
                      >
                        All ({reportModal.totalAttempted})
                      </Button>
                      <Button
                        color={reportModal.filterStatus === "SUCCESS" ? "success" : "light"}
                        className="border font-size-11"
                        onClick={() => setReportModal((prev) => ({ ...prev, filterStatus: "SUCCESS" }))}
                      >
                        ✅ Succeeded ({reportModal.successCount})
                      </Button>
                      <Button
                        color={reportModal.filterStatus === "FAILED" ? "danger" : "light"}
                        className="border font-size-11"
                        onClick={() => setReportModal((prev) => ({ ...prev, filterStatus: "FAILED" }))}
                      >
                        ❌ Failed ({reportModal.failureCount})
                      </Button>
                    </div>
                  </Col>
                </Row>

                {/* Device Logs Table */}
                {filteredLogs.length === 0 ? (
                  <div className="text-center py-4 border rounded bg-light">
                    <i className="bx bx-info-circle font-size-24 text-muted mb-2"></i>
                    <p className="mb-0 text-muted font-size-13">No device logs match your current filter.</p>
                  </div>
                ) : (
                  <div className="table-responsive border rounded" style={{ maxHeight: "420px" }}>
                    <Table hover className="table-centered table-nowrap mb-0 align-middle font-size-12">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th style={{ width: "40px" }}>#</th>
                          <th>Recipient Member</th>
                          <th>Mobile No</th>
                          <th>Role</th>
                          <th>Platform</th>
                          <th>FCM Token Snippet</th>
                          <th>Delivery Status</th>
                          <th>Exact Reason / Error Details</th>
                          <th>Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLogs.map((log, idx) => {
                          const isSuccess = String(log.Status).toUpperCase() === "SUCCESS";
                          const tokenSnippet = log.FCMToken
                            ? `${log.FCMToken.substring(0, 10)}...${log.FCMToken.substring(log.FCMToken.length - 6)}`
                            : "-";
                          return (
                            <tr key={log.Id || idx}>
                              <td className="text-muted">{idx + 1}</td>
                              <td className="font-weight-bold text-dark">
                                {log.MemberName || "App User"}
                              </td>
                              <td>{log.MobileNo || "-"}</td>
                              <td>
                                <Badge
                                  color={log.UserRole === "Driver" ? "warning" : "primary"}
                                  className="font-size-10"
                                >
                                  {log.UserRole || "Customer"}
                                </Badge>
                              </td>
                              <td>
                                <i className="bx bxl-android text-success me-1"></i>
                                {log.DevicePlatform || "Android"}
                              </td>
                              <td>
                                <code className="text-muted font-size-11" title={log.FCMToken}>
                                  {tokenSnippet}
                                </code>
                              </td>
                              <td>
                                <Badge
                                  color={isSuccess ? "success" : "danger"}
                                  className="p-1 px-2 font-size-11 rounded-pill"
                                >
                                  {isSuccess ? "✅ Succeeded" : "❌ Failed"}
                                </Badge>
                              </td>
                              <td style={{ maxWidth: "300px", whiteSpace: "normal" }}>
                                {isSuccess ? (
                                  <span className="text-success font-size-11">
                                    <i className="bx bx-check me-1"></i>
                                    Delivered to FCM Gateway
                                  </span>
                                ) : (
                                  <span className="text-danger font-size-11 font-weight-bold">
                                    <i className="bx bx-error me-1"></i>
                                    {log.ErrorMessage || log.ErrorCode || "Dispatch Failed"}
                                  </span>
                                )}
                              </td>
                              <td className="text-muted font-size-11">
                                {log.DispatchedDate
                                  ? new Date(log.DispatchedDate).toLocaleTimeString("en-IN")
                                  : "-"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter className="bg-light">
            <Button
              color="secondary"
              size="sm"
              onClick={() => setReportModal((prev) => ({ ...prev, show: false }))}
            >
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </div>
  );
};

export default PageList_CustomNotification;
